import { createPlaylistEntry, type ParseResult, type PlaylistDocument, type PlaylistEntry, type ProTVTarget } from "./types";
import { extractYouTubeVideoId } from "./url";

interface JsonEntry {
  mainUrl?: unknown;
  alternateUrl?: unknown;
  title?: unknown;
  description?: unknown;
  tags?: unknown;
  image?: unknown;
}

const text = (value: unknown): string => (typeof value === "string" ? value : "");

function parseJson(content: string): ParseResult {
  const value: unknown = JSON.parse(content);
  if (!value || typeof value !== "object" || !Array.isArray((value as { entries?: unknown }).entries)) {
    throw new Error("This JSON file does not contain a ProTV entries array.");
  }

  const root = value as { header?: unknown; entries: JsonEntry[] };
  const entries = root.entries.map((entry, index) => {
    const mainUrl = text(entry.mainUrl);
    const tags = Array.isArray(entry.tags)
      ? entry.tags.filter((tag): tag is string => typeof tag === "string")
      : text(entry.tags).split(",");
    const youtubeVideoId = extractYouTubeVideoId(mainUrl) ?? undefined;
    return createPlaylistEntry(
      {
        mainUrl,
        alternateUrl: text(entry.alternateUrl),
        title: text(entry.title),
        description: text(entry.description),
        tags,
        imagePath: text(entry.image),
        source: "import",
        youtubeVideoId,
      },
      `import-${index + 1}`,
    );
  });

  return {
    document: { header: text(root.header), entries },
    detectedTarget: "3.x",
    format: "json",
    notices: [],
  };
}

function parseBrokenReactExport(content: string): ParseResult | null {
  const lines = content.split(/\r?\n/).map((line) => line.trim()).filter(Boolean);
  if (lines.length === 0 || !lines.every((line) => /@https?:\/\//i.test(line))) return null;

  const entries = lines.map((line, index) => {
    const splitAt = line.search(/@https?:\/\//i);
    const title = line.slice(0, splitAt);
    const mainUrl = line.slice(splitAt + 1);
    return createPlaylistEntry(
      { mainUrl, title, source: "import", youtubeVideoId: extractYouTubeVideoId(mainUrl) ?? undefined },
      `react-import-${index + 1}`,
    );
  });

  return {
    document: { header: "", entries },
    detectedTarget: "2.2",
    format: "tokenized",
    notices: [{ level: "warning", message: "Recovered the non-standard name@url format written by the retired React port." }],
  };
}

function finalizeEntry(entry: PlaylistEntry | null, entries: PlaylistEntry[]): void {
  if (!entry) return;
  entries.push({
    ...entry,
    title: entry.title.trim(),
    description: entry.description.trim(),
    tags: entry.tags.map((tag) => tag.trim()).filter(Boolean),
  });
}

function parseTokenized(content: string): ParseResult {
  const recovered = parseBrokenReactExport(content);
  if (recovered) return recovered;

  const lines = content.replace(/^\uFEFF/, "").split(/\r?\n/);
  let entryToken = "@";
  let alternateToken = "^";
  let imageToken = "/";
  let tagToken = "#";
  let titleToken = "~";
  let hasExplicitTitleToken = false;
  let tokenDefinitionIndex = -1;

  const firstContentIndex = lines.findIndex((line) => line.trim().length > 0);
  const firstContent = lines[firstContentIndex]?.trim() ?? "";
  if (firstContent.startsWith("?")) {
    const symbols = [...firstContent.slice(1)];
    if (symbols.length < 5) throw new Error("The custom ProTV token definition must provide five symbols after ?.");
    [entryToken, alternateToken, imageToken, tagToken, titleToken] = symbols as [string, string, string, string, string];
    hasExplicitTitleToken = true;
    tokenDefinitionIndex = firstContentIndex;
  } else {
    hasExplicitTitleToken = lines.some((line) => line.trimStart().startsWith(titleToken));
  }

  const entries: PlaylistEntry[] = [];
  const headerLines: string[] = [];
  let current: PlaylistEntry | null = null;
  let sequence = 0;

  for (const [lineIndex, rawLine] of lines.entries()) {
    if (lineIndex === tokenDefinitionIndex) continue;
    const line = rawLine.trim();

    if (line.startsWith(entryToken)) {
      finalizeEntry(current, entries);
      sequence += 1;
      const mainUrl = line.slice(entryToken.length).trim();
      current = createPlaylistEntry(
        { mainUrl, source: "import", youtubeVideoId: extractYouTubeVideoId(mainUrl) ?? undefined },
        `import-${sequence}`,
      );
      continue;
    }

    if (!current) {
      if (line) headerLines.push(line);
      continue;
    }

    if (line.startsWith(alternateToken)) {
      current.alternateUrl = line.slice(alternateToken.length).trim();
    } else if (line.startsWith(imageToken)) {
      current.imagePath = line.slice(imageToken.length).replace(/^\/+/, "").trim();
    } else if (line.startsWith(tagToken)) {
      current.tags = line.slice(tagToken.length).split(",").map((tag) => tag.trim()).filter(Boolean);
    } else if (hasExplicitTitleToken && line.startsWith(titleToken)) {
      const nextTitle = line.slice(titleToken.length).trim();
      current.title = current.title ? `${current.title}\n${nextTitle}` : nextTitle;
    } else if (hasExplicitTitleToken) {
      current.description = current.description ? `${current.description}\n${line}` : line;
    } else {
      current.title = current.title ? `${current.title}\n${line}` : line;
    }
  }

  finalizeEntry(current, entries);
  if (entries.length === 0) throw new Error("No ProTV playlist entries were found. Entries must begin with @ by default.");

  const detectedTarget: ProTVTarget = hasExplicitTitleToken ? "3.x" : entries.some((entry) => entry.alternateUrl || entry.tags.length > 0) ? "2.3" : "2.2";
  return {
    document: { header: headerLines.join("\n"), entries },
    detectedTarget,
    format: "tokenized",
    notices: hasExplicitTitleToken
      ? []
      : [{ level: "info", message: `Detected legacy ProTV ${detectedTarget} title lines and converted them to the shared model.` }],
  };
}

export function parsePlaylist(content: string): ParseResult {
  const trimmed = content.trim();
  if (!trimmed) throw new Error("The playlist file is empty.");
  return trimmed.startsWith("{") ? parseJson(trimmed) : parseTokenized(content);
}

export function mergeDocuments(current: PlaylistDocument, incoming: PlaylistDocument): PlaylistDocument {
  return {
    header: current.header || incoming.header,
    entries: [...current.entries, ...incoming.entries],
  };
}
